import { BlogPost } from "./types";

export const reducirImagenParaDni: BlogPost = {
  slug: "como-reducir-imagen-para-dni",
  title: "Cómo reducir una imagen para DNI o documento oficial: requisitos y proceso",
  description: "Guía completa para preparar fotos de carnet para trámites oficiales: DNI, pasaporte, permisos. Dimensiones exactas, fondo correcto, tamaño de archivo y errores que pueden rechazar tu solicitud.",
  date: "2026-03-24",
  readTime: "8 min",
  keywords: "reducir imagen para dni, foto carnet digital, comprimir foto dni, foto para pasaporte digital, imagen documento oficial tamaño",
  content: `
## Por qué tu foto es rechazada en formularios oficiales

Las solicitudes de DNI, pasaporte, permisos de residencia, y otros documentos oficiales tienen requisitos fotográficos muy específicos. Un rechazo frecuente no es por la calidad visual de la foto, sino por no cumplir los requisitos técnicos:

- El archivo supera el límite de tamaño (frecuentemente 200-500KB)
- Las dimensiones no corresponden a la proporción carnet
- El fondo no es blanco uniforme
- La imagen tiene metadatos que el sistema no acepta
- El formato es incorrecto (se pide JPEG y se sube PNG)

Esta guía cubre los requisitos técnicos de los sistemas digitales más comunes en España.

## Requisitos estándar para foto de carnet digital

### Sede Electrónica — Renovación de DNI

Para solicitudes en la Sede Electrónica del Ministerio del Interior:
- **Formato:** JPEG
- **Tamaño máximo:** 200KB
- **Dimensiones mínimas:** 400×514 px (proporción carnet)
- **Proporción:** 0.78:1 (ancho:alto) — equivalente a 3×4 cm físico escalado
- **Fondo:** blanco o claro uniforme

### Pasaporte español

- **Formato:** JPEG
- **Tamaño máximo:** 200KB
- **Dimensiones recomendadas:** 600×800 px
- **Proporción:** 3:4
- **Fondo:** blanco uniforme
- **Cara:** centrada, visible de frente, ojos a nivel del 50-60% de la altura de la imagen

### Permisos de conducir y otros documentos DGT

- **Formato:** JPEG
- **Tamaño máximo:** 100-200KB según el trámite
- **Dimensiones:** 320×400 px mínimo
- **Fondo:** blanco o claro

### Formularios de administración electrónica (genéricos)

Para formularios de ayuntamientos, comunidades autónomas y otros organismos:
- **Formato:** JPEG o PNG
- **Tamaño máximo:** 200-500KB (varía por organismo)
- **Dimensiones:** normalmente no especificadas, pero debe verse bien la cara

## Requisitos visuales de la fotografía

Antes de redimensionar y comprimir, la foto debe cumplir ciertos requisitos visuales:

1. **Fondo blanco o muy claro uniforme** — sin sombras ni gradientes.

2. **Iluminación frontal uniforme** — sin sombras en la cara ni en el fondo. Lo ideal: dos fuentes de luz, una a cada lado, o luz natural difusa.

3. **Expresión neutra, boca cerrada** — los sistemas biométricos modernos verifican la expresión.

4. **Sin gafas** — la normativa de ICAO (estándar internacional de documentos de viaje) prohíbe gafas en fotos biométricas desde 2018.

5. **Cabeza erguida y centrada** — sin inclinación.

6. **Sin sombreros, gorros ni velos** (excepto por razones religiosas con documentación).

7. **La cara debe ocupar el 70-80% de la altura de la imagen.**

### Cómo hacer la foto con el móvil

1. Pega una hoja de papel blanco A4 en la pared como fondo
2. Sitúa el móvil a la altura de los ojos, a 60-80cm de distancia
3. Usa la cámara trasera (mayor resolución que la frontal)
4. Encuadra la cabeza dejando espacio en la parte superior e inferior
5. Haz varias tomas y elige la mejor

## Proceso de reducción técnica de la foto

### Paso 1: Recortar a proporción carnet

La proporción estándar para fotos de carnet es aproximadamente 3:4 (ancho:alto).

Proporciones útiles:
- 300×400 px (3:4 exacto)
- 400×514 px (proporción DNI español)
- 600×800 px (buena calidad, 3:4)

Recorta la foto para que:
- La cabeza esté centrada
- La coronilla esté a unos 20px del borde superior
- La barbilla esté a unos 80px del borde inferior
- Los hombros sean visibles en la parte inferior

### Paso 2: Ajustar dimensiones finales

Una resolución de 400×514 px es suficiente para la mayoría de trámites digitales. Esta resolución produce un archivo de excelente calidad que normalmente pesa entre 40-120KB al 85% JPEG — dentro de cualquier límite de 200KB.

### Paso 3: Comprimir al nivel correcto

1. Sube la foto recortada a nuestro [compresor de imágenes](/)
2. Selecciona calidad JPEG: 85% — suficiente para que la cara se vea nítida
3. Verifica que el peso resultante está por debajo del límite del formulario
4. Si el límite es 200KB y tu imagen pesa 180KB al 85%, perfecto. Si pesa 220KB, baja al 80%.

### Paso 4: Verificar el resultado

Antes de subir al formulario oficial:
1. Abre la imagen y verifica que la cara se ve nítida
2. Comprueba que el fondo es uniforme (sin artefactos JPEG visibles en el fondo blanco)
3. Confirma el tamaño del archivo en tu explorador de archivos
4. Confirma el formato (.jpg o .jpeg, no .png a menos que el formulario lo pida)

## Tabla de dimensiones y pesos esperados

| Dimensiones | JPEG 90% | JPEG 85% | JPEG 80% |
|---|---|---|---|
| 300×400 px | 35-55 KB | 25-40 KB | 18-30 KB |
| 400×514 px | 55-85 KB | 40-65 KB | 30-50 KB |
| 600×800 px | 120-180 KB | 90-140 KB | 70-110 KB |
| 800×1067 px | 200-300 KB | 160-220 KB | 120-170 KB |

La mayoría de formularios con límite de 200KB aceptan perfectamente fotos de 600×800px al 85% JPEG (90-140KB).

## Errores frecuentes y cómo evitarlos

### Error 1: Subir la foto directamente del móvil sin redimensionar

Una foto de selfie de un iPhone moderno tiene 3024×4032 px y pesa 3-5MB. Los formularios rechazan archivos tan grandes. Siempre redimensiona tú mismo a 400-600px de ancho para tener control sobre el resultado.

### Error 2: Fondo blanco con artefactos JPEG

Cuando comprimes una foto con fondo blanco a JPEG, el algoritmo puede crear artefactos (manchas ligeramente grises) en el área del fondo. Para fotos de carnet, JPEG al 85%+ generalmente mantiene el fondo lo suficientemente limpio. Si notas manchas en el fondo, usa JPEG 90%.

### Error 3: Proporción incorrecta

Muchos formularios realizan verificación biométrica automática. Si la proporción no es la correcta o la cara está mal encuadrada, el sistema puede rechazar la foto.

### Error 4: Foto con gafas o expresión no neutra

Los sistemas biométricos modernos verifican estos aspectos automáticamente. Si el sistema rechaza tu foto sin dar razón clara, revisa que la expresión sea neutra, sin gafas, con los ojos claramente visibles.

### Error 5: Formato incorrecto

Algunos formularios especifican exactamente "JPEG" y rechazan PNG aunque visualmente sean equivalentes. Guarda siempre como \`.jpg\` o \`.jpeg\`.

## Trámites específicos

### DNI y pasaporte presencial

La renovación presencial en comisarías es la opción más fiable para el documento físico — la foto la hace el funcionario. La foto digital se usa para trámites complementarios vía Sede Electrónica.

### Trámites de extranjería

Las solicitudes de Tarjeta de Identidad de Extranjero (TIE) y otros permisos de residencia tienen formularios en la Sede Electrónica donde se sube la foto. Los requisitos suelen ser 200KB máximo, JPEG, fondo blanco.

### Documentos universitarios y académicos

Para expedientes académicos, carnets universitarios y matrículas que piden foto digital:
- Suelen aceptar hasta 500KB
- JPEG o PNG
- Proporción carnet recomendada pero no siempre verificada automáticamente

## Preguntas frecuentes

### ¿Puedo hacer la foto del DNI yo mismo con el móvil?

Para la foto del documento físico (el carnet) la foto la hace el funcionario en la comisaría. Para solicitudes online donde piden foto digital (permisos complementarios, trámites de extranjería), sí puedes usar una foto del móvil si cumple los requisitos: fondo blanco, cara centrada, expresión neutra, sin gafas.

### ¿Qué hago si el sistema rechaza mi foto sin explicación?

Verifica en orden: 1) Tamaño del archivo (dentro del límite). 2) Formato (JPEG, no PNG). 3) Dimensiones (en rango). 4) Contenido de la foto (fondo blanco, cara visible, sin gafas). El 90% de rechazos se debe a uno de estos cuatro factores.

### ¿Las fotos en blanco y negro son válidas para trámites oficiales?

No. Los documentos biométricos modernos requieren foto en color. Las fotos en blanco y negro son rechazadas automáticamente por los sistemas de verificación.

### ¿Cómo sé si mi foto tiene el fondo suficientemente blanco?

Abre la imagen en cualquier visor y haz zoom en el área del fondo. Si el fondo es puro blanco o muy cercano (en la escala RGB debe ser 255-255-255 o muy cercano), es correcto.

### ¿El tamaño de la foto afecta la validez biométrica?

No directamente, pero las dimensiones demasiado pequeñas (menos de 300px de ancho) pueden hacer que los algoritmos biométricos no detecten correctamente los puntos de referencia facial. Usa mínimo 400×514px para asegurar compatibilidad con todos los sistemas.

## Conclusión

Para preparar una foto de carnet para trámites oficiales digitales: recorta en proporción 3:4 a una resolución de 400-600px de ancho, comprime al 85% JPEG, y verifica que el peso del archivo está por debajo del límite del formulario (normalmente 200KB).

El error más común es subir directamente la foto del móvil sin redimensionar. Con un redimensionado correcto y compresión al 85%, el resultado cumplirá todos los requisitos técnicos con calidad visual más que suficiente.

Usa nuestro [compresor de imágenes](/) para redimensionar y comprimir en un solo paso.
  `
};
